import { FulfillmentTypeGetResponseType } from "@sellar-io/api-v2/types";
import {
  Button,
  DataButton,
  Form,
  H5,
  H6,
  Icon,
  InputCurrency,
  InputContainer,
} from "@sellar-io/components";
import { useState } from "react";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { useForm, useStore } from "@tanstack/react-form";
import { generateContainerTypeDeliveryChargeRulesText } from "@sellar-io/common";
import { ContainerTypeDeliveryChargeRuleCreateDialog } from "../../deliveryCharges/containerDeliveryChargeRules/dialogs/ContainerTypeDeliveryChargeRuleCreateDialog";
import { ContainerTypeDeliveryChargeRuleUpdateDialog } from "../../deliveryCharges/containerDeliveryChargeRules/dialogs/ContainerTypeDeliveryChargeRuleUpdateDialog";
import { z } from "zod";
import { useFulfillmentTypeUpdate } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeUpdate";
import { Alert } from "../../../components/alert/Alert";
import { getFreeDeliveryBadge } from "../../../components/courierHaulierSwitchPrompt/pages/PalletDeliverySettingsPage.shared";

export const FreeDeliveryOffers = ({
  deliveryMethod,
  readOnly = false,
}: {
  deliveryMethod?: FulfillmentTypeGetResponseType;
  readOnly?: boolean;
}) => {
  const [deliveryChargeDialogActive, setDeliveryChargeDialogActive] =
    useState(false);

  const initialInputCharge = formatPriceApiToInput(
    deliveryMethod?.freeDeliveryBasketSubtotal ?? null,
  );

  const fulfillmentTypeUpdateMutation = useFulfillmentTypeUpdate(
    deliveryMethod?.id ?? 0,
  );

  const loading = fulfillmentTypeUpdateMutation.isPending;

  const formSchema = z.object({
    deliveryByBasket: z.string(),
  });

  const form = useForm({
    defaultValues: {
      deliveryByBasket: initialInputCharge,
    },
    onSubmit: ({ value }) => {
      const data = formSchema.parse(value);
      const apiValue = formatPriceInputToApi(data.deliveryByBasket);

      return fulfillmentTypeUpdateMutation.mutate({
        freeDeliveryBasketSubtotal: apiValue,
      });
    },
  });

  const deliveryByBasket = useStore(
    form.store,
    (state) => state.values.deliveryByBasket,
  );

  const deliveryByBasketUpdated = deliveryByBasket !== initialInputCharge;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex max-w-md flex-col">
          <H5>By basket subtotal</H5>
          <H6>
            Offer free delivery for the customer's entire order when it exceeds
            the specified amount (before VAT and discounts)
          </H6>
        </div>
        <Form
          className={"flex gap-2"}
          onSubmit={() => {
            form.handleSubmit();
          }}
        >
          <form.Field name="deliveryByBasket">
            {({ state, handleBlur, handleChange }) => (
              <InputContainer className="py-2">
                <InputCurrency
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    handleChange(e.target.value);
                  }}
                  disabled={readOnly}
                />
                {deliveryMethod?.fulfillmentType === "haulier" && (
                  <span className="flex-none">
                    {getFreeDeliveryBadge(
                      deliveryMethod.freeDeliveryBasketSubtotal ?? undefined,
                      true,
                    )}
                  </span>
                )}
              </InputContainer>
            )}
          </form.Field>
          {!readOnly && (
            <DataButton
              border={true}
              type="submit"
              disabled={!deliveryByBasketUpdated}
              loading={loading}
            >
              Save
            </DataButton>
          )}
        </Form>
      </div>
      {deliveryMethod?.fulfillmentType === "haulier" ? (
        <Alert
          type="sellar"
          title="We recommend offering free delivery when customers spend £500 or more."
          message={"Encourage larger orders and make deliveries more efficient"}
          className="w-full bg-zinc-50"
        />
      ) : null}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <H5>By container type</H5>
            <H6>
              Offer free delivery on specific container types when a customer
              spends the specified amount on them (before VAT and discounts)
            </H6>
          </div>

          {!readOnly && (
            <DataButton
              border={true}
              onClick={() => setDeliveryChargeDialogActive(true)}
            >
              <Icon icon="PLUS" />
              New
            </DataButton>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {deliveryMethod?.ContainerTypeDeliveryChargeRules.map(
            (containerTypeDeliveryChargeRule) => (
              <FreeDeliveryCard
                key={containerTypeDeliveryChargeRule.id}
                containerTypeDeliveryChargeRule={
                  containerTypeDeliveryChargeRule
                }
                readOnly={readOnly}
              />
            ),
          )}
        </div>
      </div>
      <ContainerTypeDeliveryChargeRuleCreateDialog
        active={deliveryChargeDialogActive}
        onDismiss={() => setDeliveryChargeDialogActive(false)}
        fulfillmentType={deliveryMethod?.fulfillmentType ?? "direct"}
        fulfillmentTypeId={deliveryMethod?.id}
      />
    </>
  );
};

const FreeDeliveryCard = ({
  containerTypeDeliveryChargeRule,
  readOnly = false,
}: {
  containerTypeDeliveryChargeRule: FulfillmentTypeGetResponseType["ContainerTypeDeliveryChargeRules"][number];
  readOnly?: boolean;
}) => {
  const [active, setActive] = useState(false);

  return (
    <>
      <div
        className="flex w-full cursor-pointer items-center justify-between rounded-lg bg-white px-4 py-2 hover:bg-zinc-50"
        onClick={() => setActive(!readOnly)}
      >
        <p>
          {generateContainerTypeDeliveryChargeRulesText(
            containerTypeDeliveryChargeRule,
          )}
        </p>
        {!readOnly && (
          <Button
            color="transparent"
            size="small"
            className="flex items-center gap-2"
          >
            Edit <Icon icon="CHEVRON_RIGHT" />
          </Button>
        )}
      </div>
      <ContainerTypeDeliveryChargeRuleUpdateDialog
        active={active}
        onDismiss={() => setActive(false)}
        existingDeliveryChargeRule={containerTypeDeliveryChargeRule}
      />
    </>
  );
};
